import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from '../services/onboardingApi';
import { OnboardingProject, OnboardingInsights, AccountConfiguration } from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, CheckCircle2, AlertTriangle, Zap, Filter, Download, Pencil, Search, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ConfigurationModal, ImportedAccountForConfig, AccountConfigurationData } from '../components/ConfigurationModal';
import { BulkConfigurationModal, BulkAccountForConfig } from '../components/BulkConfigurationModal';
import { useImportedAccounts, useAccountConfigurations, useSaveAccountConfiguration, useBulkSaveConfigurations } from '../hooks/useAccountConfiguration';
import { supabase } from '@/integrations/supabase/client';
import { ExportReimportPanel } from '../components/ExportReimportPanel';
import { cn } from '@/lib/utils';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

interface DataQualityIssue {
  id: string;
  type: 'critical' | 'warning';
  category: string;
  count: number;
  description: string;
  affectedEntities: string[];
  canAutoFix: boolean;
}

interface AccountWithConfig {
  id: string;
  account_number?: string;
  property_name?: string;
  unit_number?: string;
  commodity_type?: string;
  status: 'complete' | 'partial' | 'incomplete';
  collection_configured: boolean;
  parsing_configured: boolean;
  configuration?: AccountConfigurationData;
}

const Stage3Validation = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, setProject } = useOutletContext<OutletContext>();

  const [insights, setInsights] = useState<OnboardingInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState<DataQualityIssue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<DataQualityIssue | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [bulkClassificationMode, setBulkClassificationMode] = useState(false);

  // Account Configuration State  
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [editingAccount, setEditingAccount] = useState<ImportedAccountForConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState<'all' | 'complete' | 'partial' | 'incomplete'>('all');
  
  // Staged data from Stage2
  const [stagedDataRows, setStagedDataRows] = useState<any[]>([]);
  const [loadingStagedData, setLoadingStagedData] = useState(false);
  const [importBatchId, setImportBatchId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);

  // Load accounts and configurations from database
  const { data: importedAccounts, isLoading: loadingAccounts } = useImportedAccounts(projectId);
  const { data: configurations } = useAccountConfigurations(projectId);
  const saveConfigMutation = useSaveAccountConfiguration();
  const bulkSaveMutation = useBulkSaveConfigurations();

  // Merge accounts with their configurations
  const accounts = useMemo<AccountWithConfig[]>(() => {
    if (!importedAccounts) return [];
    
    return importedAccounts.map(account => {
      const config = configurations?.find(c => c.imported_account_id === account.id);
      const collectionConfigured = config?.collection_completed || false;
      const parsingConfigured = config?.parsing_completed || false;
      
      let status: 'complete' | 'partial' | 'incomplete' = 'incomplete';
      if (collectionConfigured && parsingConfigured) {
        status = 'complete';
      } else if (collectionConfigured || parsingConfigured) {
        status = 'partial';
      }

      return {
        id: account.id,
        account_number: account.account_number,
        property_name: account.property_name,
        unit_number: account.unit_number,
        commodity_type: account.commodity_type,
        status,
        collection_configured: collectionConfigured,
        parsing_configured: parsingConfigured,
        configuration: config ? {
          collection_source: config.collection_source,
          collection_frequency: config.collection_frequency,
          collection_config: config.collection_config,
          collection_completed: config.collection_completed,
          parsing_fields: config.parsing_fields,
          parsing_completed: config.parsing_completed,
          apply_to_property: config.apply_to_property,
        } as AccountConfigurationData : undefined,
      };
    });
  }, [importedAccounts, configurations]);

  useEffect(() => {
    if (project && projectId && projectId !== 'new') {
      loadInsights();
      loadStagedData();
    }
  }, [project, projectId]);

  // Load staged data from Stage2 - only load rows marked as ready_for_next_stage
  const loadStagedData = async () => {
    if (!project) return;
    
    setLoadingStagedData(true);
    try {
      // Get ALL import batches for this project (not just 'properties')
      const batches = await onboardingApi.listImportBatches(project.id);
      const allBatchIds = batches.map(b => b.id);
      
      if (allBatchIds.length === 0) {
        console.log('No import batches found for project');
        setStagedDataRows([]);
        return;
      }
      
      // Use the latest batch ID for importBatchId (for ExportReimportPanel)
      const latestBatch = batches.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      setImportBatchId(latestBatch.id);
      
      // CRITICAL FIX: Load ALL rows ready for validation from ALL batches (NO LIMIT) using chunked loading
      // First get total count across all batches
      // Add fallback if ready_for_next_stage column doesn't exist
      let totalCount = 0;
      let countError: any = null;
      
      try {
        const result = await (supabase as any)
          .from('tum_onboarding_staged_data')
          .select('id', { count: 'exact', head: true })
          .in('batch_id', allBatchIds)
          .eq('ready_for_next_stage', true);
        totalCount = result.count || 0;
        countError = result.error;
      } catch (error: any) {
        // If column doesn't exist, try fallback query
        if (error?.message?.includes('column') || error?.code === '42703' || countError?.code === '42703') {
          console.warn('[Stage3Validation] ready_for_next_stage column not found, using fallback query');
          const fallbackResult = await (supabase as any)
            .from('tum_onboarding_staged_data')
            .select('id', { count: 'exact', head: true })
            .in('batch_id', allBatchIds)
            .eq('validation_status', 'valid');
          totalCount = fallbackResult.count || 0;
          countError = fallbackResult.error;
        } else {
          countError = error;
        }
      }
      
      if (countError) {
        console.error('Error counting validation rows:', countError);
        throw countError;
      }
      
      console.log(`📊 Loading ${totalCount} rows for Validation stage (from ${allBatchIds.length} batches)`);
      
      // Fetch all rows in chunks (Supabase PostgREST default max is 1000)
      // REMOVED MAX_ROWS LIMIT - Load ALL rows regardless of count
      const CHUNK_SIZE = 1000;
      const allStagedData: any[] = [];
      const rowsToLoad = totalCount || 0;
      
      // Determine if ready_for_next_stage column exists based on count query result
      const useReadyForNextStage = totalCount > 0 || countError === null;
      
      for (let offset = 0; offset < rowsToLoad; offset += CHUNK_SIZE) {
        let chunkData: any[] = [];
        let chunkError: any = null;
        
        try {
          const result = await (supabase as any)
            .from('tum_onboarding_staged_data')
            .select('id, onboarding_task_id, row_number, mapped_data, validation_status, validation_issues, import_status, ready_for_next_stage, batch_id')
            .in('batch_id', allBatchIds)
            .eq('ready_for_next_stage', true)
            .order('row_number', { ascending: true })
            .range(offset, offset + CHUNK_SIZE - 1);
          chunkData = result.data || [];
          chunkError = result.error;
        } catch (error: any) {
          // If column doesn't exist, use fallback query
          if (error?.message?.includes('column') || error?.code === '42703' || chunkError?.code === '42703') {
            console.warn(`[Stage3Validation] ready_for_next_stage column not found at offset ${offset}, using fallback`);
            const fallbackResult = await (supabase as any)
              .from('tum_onboarding_staged_data')
              .select('id, row_number, mapped_data, validation_status, validation_issues, import_status, batch_id')
              .in('batch_id', allBatchIds)
              .eq('validation_status', 'valid')
              .order('row_number', { ascending: true })
              .range(offset, offset + CHUNK_SIZE - 1);
            chunkData = fallbackResult.data || [];
            chunkError = fallbackResult.error;
          } else {
            chunkError = error;
          }
        }
        
        if (chunkError) {
          console.error(`Error loading chunk at offset ${offset}:`, chunkError);
          throw chunkError;
        }
        
        if (chunkData && chunkData.length > 0) {
          allStagedData.push(...chunkData);
          console.log(`📥 Loaded ${chunkData.length} rows (total: ${allStagedData.length}/${rowsToLoad})`);
        }
      }
      
      console.log(`✅ Loaded ${allStagedData.length} total rows for Validation stage`);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:253',message:'Staged data loaded for validation',data:{totalRows:allStagedData.length,batchIds:allBatchIds.length,firstRowId:allStagedData[0]?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      setStagedDataRows(allStagedData);
    } catch (error) {
      console.error('Failed to load staged data:', error);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:257',message:'Error loading staged data',data:{errorMessage:error instanceof Error?error.message:String(error)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      setStagedDataRows([]);
    } finally {
      setLoadingStagedData(false);
    }
  };

  const loadInsights = async () => {
    if (!project) return;

    try {
      setLoading(true);
      const data = await onboardingApi.getInsights(project.id);
      setInsights(data);
      
      // Generate issues from insights
      if (data) {
        generateIssues(data);
      } else {
        // No insights yet - this is OK, just use empty state
        console.log('No insights found for project yet - this is normal for new projects');
        generateMockIssues(); // Use mock data for UI display
      }
    } catch (error: any) {
      console.error('Failed to load insights:', error);
      // Don't show error to user - just use mock data
      // This is expected if the table doesn't exist or RLS blocks access
      generateMockIssues();
    } finally {
      setLoading(false);
    }
  };

  const generateIssues = (insightsData: OnboardingInsights) => {
    const generatedIssues: DataQualityIssue[] = [];

    // Missing utility type classification
    if (insightsData.accounts_missing_utility_type > 0) {
      generatedIssues.push({
        id: 'missing_utility_type',
        type: 'critical',
        category: 'Classification',
        count: insightsData.accounts_missing_utility_type,
        description: 'Accounts missing utility type classification',
        affectedEntities: ['accounts'],
        canAutoFix: true,
      });
    }

    // Missing bill sources
    const accountsWithoutBillSource = insightsData.total_accounts - insightsData.accounts_with_bill_source;
    if (accountsWithoutBillSource > 0) {
      generatedIssues.push({
        id: 'missing_bill_sources',
        type: 'critical',
        category: 'Configuration',
        count: accountsWithoutBillSource,
        description: 'Accounts without bill source configuration',
        affectedEntities: ['accounts'],
        canAutoFix: false,
      });
    }

    // Missing GL codes
    const accountsWithoutGLCode = insightsData.total_accounts - insightsData.accounts_with_gl_code;
    if (accountsWithoutGLCode > 0) {
      generatedIssues.push({
        id: 'missing_gl_codes',
        type: 'warning',
        category: 'Configuration',
        count: accountsWithoutGLCode,
        description: 'Accounts without GL code mappings',
        affectedEntities: ['accounts'],
        canAutoFix: false,
      });
    }

    // Data completeness warnings
    if (insightsData.warnings > 0) {
      generatedIssues.push({
        id: 'data_warnings',
        type: 'warning',
        category: 'Data Quality',
        count: insightsData.warnings,
        description: 'Data quality warnings detected',
        affectedEntities: ['properties', 'units', 'accounts', 'tenants'],
        canAutoFix: false,
      });
    }

    setIssues(generatedIssues);
  };

  const generateMockIssues = () => {
    // Mock data for demonstration
    setIssues([
      {
        id: 'missing_utility_type',
        type: 'critical',
        category: 'Classification',
        count: 597,
        description: 'Accounts missing utility type classification',
        affectedEntities: ['accounts'],
        canAutoFix: true,
      },
      {
        id: 'orphaned_units',
        type: 'critical',
        category: 'Relationships',
        count: 8,
        description: 'Units not linked to any property',
        affectedEntities: ['units'],
        canAutoFix: false,
      },
      {
        id: 'missing_external_ids',
        type: 'warning',
        category: 'Data Quality',
        count: 123,
        description: 'Accounts missing external reference IDs',
        affectedEntities: ['accounts'],
        canAutoFix: false,
      },
      {
        id: 'duplicate_accounts',
        type: 'warning',
        category: 'Data Quality',
        count: 5,
        description: 'Duplicate account numbers detected',
        affectedEntities: ['accounts'],
        canAutoFix: false,
      },
    ]);
  };

  const handleBulkClassification = () => {
    setBulkClassificationMode(true);
    setSelectedIssue(issues.find((i) => i.id === 'missing_utility_type') || null);
  };

  const handleAutoFix = async (issue: DataQualityIssue) => {
    if (!issue.canAutoFix) {
      toast({
        title: 'Cannot Auto-Fix',
        description: 'This issue requires manual intervention',
        variant: 'destructive',
      });
      return;
    }

    try {
      // TODO: Implement auto-fix logic via backend
      toast({
        title: 'Auto-Fix Initiated',
        description: `Processing ${issue.count} ${issue.affectedEntities.join(', ')}...`,
      });
      
      // Simulate processing
      setTimeout(() => {
        toast({
          title: 'Auto-Fix Complete',
          description: `Successfully fixed ${issue.count} items`,
        });
        loadInsights();
      }, 2000);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to auto-fix issue',
        variant: 'destructive',
      });
    }
  };

  const filteredIssues = filterCategory === 'all'
    ? issues
    : issues.filter((issue) => issue.category === filterCategory);

  const criticalIssues = filteredIssues.filter((i) => i.type === 'critical');
  const warnings = filteredIssues.filter((i) => i.type === 'warning');
  
  // Only block on mandatory field issues (not all critical issues)
  const mandatoryFieldIssues = criticalIssues.filter(issue => {
    // Only block on issues related to mandatory fields
    const mandatoryFieldRelated = [
      'missing_utility_type', // commodity_type is mandatory
      'missing_account_number', // account_number is mandatory
      'missing_property_name', // property_name is mandatory
      'missing_unit_number', // unit_number is mandatory
      'missing_vendor_name', // vendor_name is mandatory
    ];
    return mandatoryFieldRelated.includes(issue.id);
  });

  // Check parsing and collection mapping completion
  const mappingCompletionStatus = useMemo(() => {
    const total = accounts.length;
    const complete = accounts.filter(a => a.collection_configured && a.parsing_configured).length;
    const partial = accounts.filter(a => (a.collection_configured || a.parsing_configured) && !(a.collection_configured && a.parsing_configured)).length;
    const incomplete = accounts.filter(a => !a.collection_configured && !a.parsing_configured).length;
    
    return {
      total,
      complete,
      partial,
      incomplete,
      allComplete: total > 0 && incomplete === 0 && partial === 0,
    };
  }, [accounts]);

  // Normalize account number for matching (trim, lowercase)
  const normalizeAccountNumber = (accountNumber: string | undefined): string => {
    if (!accountNumber) return '';
    return accountNumber.toString().trim().toLowerCase();
  };

  // Normalize mapped_data to handle field name aliases
  // Stage 2 may use utility_type/supplier_name, but Stage 3 expects commodity_type/vendor_name
  const normalizeMappedData = (mappedData: Record<string, any>): Record<string, any> => {
    if (!mappedData) return {};
    
    const normalized = { ...mappedData };
    
    // Normalize utility_type -> commodity_type
    if (normalized.utility_type && !normalized.commodity_type) {
      normalized.commodity_type = normalized.utility_type;
    }
    // Keep both for backward compatibility
    if (normalized.commodity_type && !normalized.utility_type) {
      normalized.utility_type = normalized.commodity_type;
    }
    
    // Normalize supplier_name -> vendor_name
    if (normalized.supplier_name && !normalized.vendor_name) {
      normalized.vendor_name = normalized.supplier_name;
    }
    // Keep both for backward compatibility
    if (normalized.vendor_name && !normalized.supplier_name) {
      normalized.supplier_name = normalized.vendor_name;
    }
    
    return normalized;
  };

  // Get field value with alias support
  const getFieldValue = (mappedData: Record<string, any>, field: string): any => {
    const normalized = normalizeMappedData(mappedData);
    
    // Handle field aliases
    if (field === 'commodity_type') {
      return normalized.commodity_type || normalized.utility_type || '';
    }
    if (field === 'vendor_name') {
      return normalized.vendor_name || normalized.supplier_name || '';
    }
    
    return normalized[field] || '';
  };

  // Handle cell edit in validation table
  const handleCellEdit = async (rowId: string, field: string, value: any) => {
    try {
      const row = stagedDataRows.find(r => r.id === rowId);
      if (!row) return;

      const mappedData = { ...(row.mapped_data || {}) };
      
      // Set the primary field name
      mappedData[field] = value;
      
      // Also set aliases for backward compatibility
      if (field === 'commodity_type') {
        mappedData.utility_type = value;
      } else if (field === 'utility_type') {
        mappedData.commodity_type = value;
      } else if (field === 'vendor_name') {
        mappedData.supplier_name = value;
      } else if (field === 'supplier_name') {
        mappedData.vendor_name = value;
      }

      // Update in database
      const { error } = await (supabase as any)
        .from('tum_onboarding_staged_data')
        .update({ mapped_data: mappedData })
        .eq('id', rowId);

      if (error) throw error;

      // Update local state
      setStagedDataRows(prev => prev.map(r => 
        r.id === rowId ? { ...r, mapped_data: mappedData } : r
      ));

      toast({
        title: 'Updated',
        description: `${field} updated successfully`,
      });
    } catch (error: any) {
      console.error('Failed to update cell:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update cell',
        variant: 'destructive',
      });
    }
  };
  
  // Calculate summary statistics from staged data
  const dataSummary = useMemo(() => {
    if (stagedDataRows.length === 0) return null;
    
    const total = stagedDataRows.length;
    const valid = stagedDataRows.filter(r => r.validation_status === 'valid').length;
    const invalid = stagedDataRows.filter(r => r.validation_status === 'invalid').length;
    const warning = stagedDataRows.filter(r => r.validation_status === 'warning').length;
    
    // Count rows with missing mandatory fields (using normalized field access)
    const mandatoryFields = ['account_number', 'commodity_type', 'property_name', 'unit_number', 'vendor_name'];
    const missingMandatory = stagedDataRows.filter(row => {
      const mapped = normalizeMappedData(row.mapped_data || {});
      return mandatoryFields.some(field => {
        const value = getFieldValue(mapped, field);
        return !value || value === '';
      });
    }).length;
    
    // Count rows with vendor not found
    const vendorNotFound = stagedDataRows.filter(row => {
      const issues = row.validation_issues || [];
      return issues.some((issue: any) => 
        issue.message?.toLowerCase().includes('vendor') && 
        (issue.message?.toLowerCase().includes('not found') || issue.message?.toLowerCase().includes('missing'))
      );
    }).length;
    
    return {
      total,
      valid,
      invalid,
      warning,
      missingMandatory,
      vendorNotFound,
      readyForNext: valid,
    };
  }, [stagedDataRows]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading validation data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto space-y-6 px-4 overflow-x-hidden">
      <div>
        <h2 className="text-2xl font-semibold">Stage 3: Validation & Matching</h2>
        <p className="text-muted-foreground mt-1">
          Ensure data quality and completeness before configuration
        </p>
      </div>

      {/* CRITICAL: Insights Banner - Parsing & Collection Mapping Requirements */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-2 text-blue-900">
                ⚠️ Important: Parsing & Collection Mapping Required
              </h3>
              <p className="text-sm text-blue-800 mb-3">
                Before proceeding to the next stage, <strong>each row must have both:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-blue-800 mb-4">
                <li><strong>Collection Mapping:</strong> Configure how bills are collected (source, frequency, settings)</li>
                <li><strong>Parsing Mapping:</strong> Configure which fields to extract from bills (account number, dates, amounts, etc.)</li>
              </ul>
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="border-blue-600 text-blue-700">
                  {accounts.filter(a => a.collection_configured && a.parsing_configured).length} / {accounts.length} Complete
                </Badge>
                <Badge variant="outline" className="border-amber-600 text-amber-700">
                  {accounts.filter(a => (a.collection_configured || a.parsing_configured) && !(a.collection_configured && a.parsing_configured)).length} Partial
                </Badge>
                <Badge variant="outline" className="border-red-600 text-red-700">
                  {accounts.filter(a => !a.collection_configured && !a.parsing_configured).length} Missing
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Quality Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Completeness</p>
                <p className="text-2xl font-bold">
                  {insights?.data_completeness_score?.toFixed(0) || '0'}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <Progress
              value={insights?.data_completeness_score || 0}
              className="mt-4"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Readiness Score</p>
                <p className="text-2xl font-bold">
                  {insights?.readiness_score?.toFixed(0) || '0'}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Zap className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <Progress
              value={insights?.readiness_score || 0}
              className="mt-4"
            />
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Critical Issues</p>
                <p className="text-2xl font-bold text-red-700">
                  {insights?.critical_issues || criticalIssues.length}
                </p>
              </div>
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Warnings</p>
                <p className="text-2xl font-bold text-amber-700">
                  {insights?.warnings || warnings.length}
                </p>
              </div>
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entity Summary */}
      {/* Data Summary Section */}
      {dataSummary && (
        <Card>
          <CardHeader>
            <CardTitle>Imported Data Summary</CardTitle>
            <CardDescription>
              Overview of all imported rows from Data Import stage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-4 border rounded-lg bg-blue-50">
                <p className="text-2xl font-bold text-blue-700">{dataSummary.total}</p>
                <p className="text-sm text-muted-foreground">Total Rows Imported</p>
              </div>
              <div className="text-center p-4 border rounded-lg bg-green-50">
                <p className="text-2xl font-bold text-green-700">{dataSummary.valid}</p>
                <p className="text-sm text-muted-foreground">Valid Rows</p>
              </div>
              <div className="text-center p-4 border rounded-lg bg-amber-50">
                <p className="text-2xl font-bold text-amber-700">{dataSummary.warning}</p>
                <p className="text-sm text-muted-foreground">Rows with Warnings</p>
              </div>
              <div className="text-center p-4 border rounded-lg bg-red-50">
                <p className="text-2xl font-bold text-red-700">{dataSummary.invalid}</p>
                <p className="text-sm text-muted-foreground">Rows with Errors</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-3 border rounded bg-muted/30">
                <p className="text-lg font-semibold text-red-600">{dataSummary.missingMandatory}</p>
                <p className="text-xs text-muted-foreground">Rows with Missing Mandatory Fields</p>
              </div>
              <div className="text-center p-3 border rounded bg-muted/30">
                <p className="text-lg font-semibold text-amber-600">{dataSummary.vendorNotFound}</p>
                <p className="text-xs text-muted-foreground">Rows with Vendor Not Found</p>
              </div>
              <div className="text-center p-3 border rounded bg-green-50">
                <p className="text-lg font-semibold text-green-700">{dataSummary.readyForNext}</p>
                <p className="text-xs text-muted-foreground">Rows Ready for Next Stage</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Data Table - Shows imported staged data with missing field highlighting */}
      {stagedDataRows.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Imported Data Validation</CardTitle>
                <CardDescription>
                  Review imported data rows and identify missing required fields
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {stagedDataRows.length} rows
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const csv = [
                      ['Row #', 'Account #', 'Property', 'Unit', 'Commodity', 'Vendor', 'Status', 'Missing Fields'].join(','),
                      ...stagedDataRows.map(row => {
                        const mapped = normalizeMappedData(row.mapped_data || {});
                        const mandatoryFields = ['account_number', 'commodity_type', 'property_name', 'unit_number', 'vendor_name'];
                        const missing = mandatoryFields.filter(f => {
                          const value = getFieldValue(mapped, f);
                          return !value || value === '';
                        });
                        return [
                          row.row_number || '',
                          getFieldValue(mapped, 'account_number'),
                          getFieldValue(mapped, 'property_name'),
                          getFieldValue(mapped, 'unit_number'),
                          getFieldValue(mapped, 'commodity_type'),
                          getFieldValue(mapped, 'vendor_name'),
                          row.validation_status || 'unknown',
                          missing.join('; ')
                        ].join(',');
                      })
                    ].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `validation-data-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-[60px]">Row #</TableHead>
                    <TableHead>Account #</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Commodity</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Collection</TableHead>
                    <TableHead>Parsing</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Missing Fields</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingStagedData ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        <TableCell><div className="h-4 w-8 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                        <TableCell><div className="h-5 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                        <TableCell><div className="h-5 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                        <TableCell><div className="h-5 w-16 bg-muted animate-pulse rounded-full" /></TableCell>
                        <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                      </TableRow>
                    ))
                  ) : stagedDataRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        No data imported yet. Complete Stage 2 to import data.
                      </TableCell>
                    </TableRow>
                  ) : (
                    stagedDataRows.map((row) => {
                      const mapped = normalizeMappedData(row.mapped_data || {});
                      const mandatoryFields = [
                        { key: 'account_number', label: 'Account #' },
                        { key: 'commodity_type', label: 'Commodity' },
                        { key: 'property_name', label: 'Property' },
                        { key: 'unit_number', label: 'Unit' },
                        { key: 'vendor_name', label: 'Vendor' }
                      ];
                      const missing = mandatoryFields.filter(f => {
                        const value = getFieldValue(mapped, f.key);
                        return !value || value === '';
                      });
                      const hasMissing = missing.length > 0;
                      const validationStatus = row.validation_status || 'unknown';
                      
                      // Find account configuration for this row
                      const accountConfig = configurations?.find(c => {
                        // Match by account number from staged data
                        const accountNumber = normalizeAccountNumber(getFieldValue(mapped, 'account_number'));
                        const importedAccount = importedAccounts?.find(a => 
                          normalizeAccountNumber(a.account_number) === accountNumber
                        );
                        if (!importedAccount && accountNumber) {
                          // Log warning for debugging (only in development)
                          if (process.env.NODE_ENV === 'development') {
                            console.debug(`[Stage3Validation] No imported account found for account number: ${getFieldValue(mapped, 'account_number')}`);
                          }
                        }
                        return importedAccount && c.imported_account_id === importedAccount.id;
                      });
                      const collectionConfigured = accountConfig?.collection_completed || false;
                      const parsingConfigured = accountConfig?.parsing_completed || false;
                      
                      return (
                        <TableRow 
                          key={row.id}
                          className={hasMissing ? 'bg-red-50/50 hover:bg-red-50' : validationStatus === 'warning' ? 'bg-amber-50/50 hover:bg-amber-50' : ''}
                        >
                          <TableCell className="font-mono text-xs">{row.row_number || '-'}</TableCell>
                          <TableCell 
                            className={cn(
                              !getFieldValue(mapped, 'account_number') ? 'bg-red-100 font-semibold' : '',
                              editingCell?.rowId === row.id && editingCell?.field === 'account_number' ? 'p-0' : 'cursor-pointer'
                            )}
                            onDoubleClick={() => setEditingCell({ rowId: row.id, field: 'account_number' })}
                          >
                            {editingCell?.rowId === row.id && editingCell?.field === 'account_number' ? (
                              <Input
                                defaultValue={getFieldValue(mapped, 'account_number')}
                                onBlur={(e) => {
                                  handleCellEdit(row.id, 'account_number', e.target.value);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, 'account_number', e.currentTarget.value);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              getFieldValue(mapped, 'account_number') || <span className="text-red-600 italic">Missing</span>
                            )}
                          </TableCell>
                          <TableCell 
                            className={cn(
                              !getFieldValue(mapped, 'property_name') ? 'bg-red-100 font-semibold' : '',
                              editingCell?.rowId === row.id && editingCell?.field === 'property_name' ? 'p-0' : 'cursor-pointer'
                            )}
                            onDoubleClick={() => setEditingCell({ rowId: row.id, field: 'property_name' })}
                          >
                            {editingCell?.rowId === row.id && editingCell?.field === 'property_name' ? (
                              <Input
                                defaultValue={getFieldValue(mapped, 'property_name')}
                                onBlur={(e) => {
                                  handleCellEdit(row.id, 'property_name', e.target.value);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, 'property_name', e.currentTarget.value);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              getFieldValue(mapped, 'property_name') || <span className="text-red-600 italic">Missing</span>
                            )}
                          </TableCell>
                          <TableCell 
                            className={cn(
                              !getFieldValue(mapped, 'unit_number') ? 'bg-red-100 font-semibold' : '',
                              editingCell?.rowId === row.id && editingCell?.field === 'unit_number' ? 'p-0' : 'cursor-pointer'
                            )}
                            onDoubleClick={() => setEditingCell({ rowId: row.id, field: 'unit_number' })}
                          >
                            {editingCell?.rowId === row.id && editingCell?.field === 'unit_number' ? (
                              <Input
                                defaultValue={getFieldValue(mapped, 'unit_number')}
                                onBlur={(e) => {
                                  handleCellEdit(row.id, 'unit_number', e.target.value);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, 'unit_number', e.currentTarget.value);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              getFieldValue(mapped, 'unit_number') || <span className="text-red-600 italic">Missing</span>
                            )}
                          </TableCell>
                          <TableCell 
                            className={cn(
                              !getFieldValue(mapped, 'commodity_type') ? 'bg-red-100 font-semibold' : '',
                              editingCell?.rowId === row.id && editingCell?.field === 'commodity_type' ? 'p-0' : 'cursor-pointer'
                            )}
                            onDoubleClick={() => setEditingCell({ rowId: row.id, field: 'commodity_type' })}
                          >
                            {editingCell?.rowId === row.id && editingCell?.field === 'commodity_type' ? (
                              <Input
                                defaultValue={getFieldValue(mapped, 'commodity_type')}
                                onBlur={(e) => {
                                  handleCellEdit(row.id, 'commodity_type', e.target.value);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, 'commodity_type', e.currentTarget.value);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              getFieldValue(mapped, 'commodity_type') || <span className="text-red-600 italic">Missing</span>
                            )}
                          </TableCell>
                          <TableCell 
                            className={cn(
                              !getFieldValue(mapped, 'vendor_name') ? 'bg-red-100 font-semibold' : '',
                              editingCell?.rowId === row.id && editingCell?.field === 'vendor_name' ? 'p-0' : 'cursor-pointer'
                            )}
                            onDoubleClick={() => setEditingCell({ rowId: row.id, field: 'vendor_name' })}
                          >
                            {editingCell?.rowId === row.id && editingCell?.field === 'vendor_name' ? (
                              <Input
                                defaultValue={getFieldValue(mapped, 'vendor_name')}
                                onBlur={(e) => {
                                  handleCellEdit(row.id, 'vendor_name', e.target.value);
                                  setEditingCell(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellEdit(row.id, 'vendor_name', e.currentTarget.value);
                                    setEditingCell(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              getFieldValue(mapped, 'vendor_name') || <span className="text-red-600 italic">Missing</span>
                            )}
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              // #region agent log
                              fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:991',message:'Collection cell clicked',data:{rowId:row.id,accountNumber:getFieldValue(mapped, 'account_number'),collectionConfigured},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                              // #endregion
                              // Find the imported account for this row
                              const accountNumber = normalizeAccountNumber(getFieldValue(mapped, 'account_number'));
                              const importedAccount = importedAccounts?.find(a => 
                                normalizeAccountNumber(a.account_number) === accountNumber
                              );
                              if (importedAccount) {
                                // #region agent log
                                fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1003',message:'Opening ConfigurationModal for Collection',data:{accountId:importedAccount.id,accountNumber:importedAccount.account_number},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                                // #endregion
                                setEditingAccount({
                                  id: importedAccount.id,
                                  account_number: importedAccount.account_number,
                                  property_name: importedAccount.property_name,
                                  unit_number: importedAccount.unit_number,
                                  commodity_type: importedAccount.commodity_type,
                                });
                                setIsModalOpen(true);
                              } else {
                                // #region agent log
                                fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1012',message:'Account not found for Collection click',data:{accountNumber:getFieldValue(mapped, 'account_number'),availableAccounts:importedAccounts?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                                // #endregion
                                toast({
                                  title: 'Account Not Found',
                                  description: 'Unable to find imported account. Please ensure data was imported correctly.',
                                  variant: 'destructive',
                                });
                              }
                            }}
                          >
                            <Badge
                              variant={collectionConfigured ? 'default' : 'destructive'}
                              className={cn(
                                collectionConfigured ? 'bg-green-600' : '',
                                'cursor-pointer'
                              )}
                            >
                              {collectionConfigured ? '✓ Configured' : '❌ Missing - Click to Configure'}
                            </Badge>
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              // #region agent log
                              fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1027',message:'Parsing cell clicked',data:{rowId:row.id,accountNumber:getFieldValue(mapped, 'account_number'),parsingConfigured},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                              // #endregion
                              // Find the imported account for this row
                              const accountNumber = normalizeAccountNumber(getFieldValue(mapped, 'account_number'));
                              const importedAccount = importedAccounts?.find(a => 
                                normalizeAccountNumber(a.account_number) === accountNumber
                              );
                              if (importedAccount) {
                                // #region agent log
                                fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1039',message:'Opening ConfigurationModal for Parsing',data:{accountId:importedAccount.id,accountNumber:importedAccount.account_number},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                                // #endregion
                                setEditingAccount({
                                  id: importedAccount.id,
                                  account_number: importedAccount.account_number,
                                  property_name: importedAccount.property_name,
                                  unit_number: importedAccount.unit_number,
                                  commodity_type: importedAccount.commodity_type,
                                });
                                setIsModalOpen(true);
                              } else {
                                // #region agent log
                                fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1051',message:'Account not found for Parsing click',data:{accountNumber:getFieldValue(mapped, 'account_number'),availableAccounts:importedAccounts?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
                                // #endregion
                                toast({
                                  title: 'Account Not Found',
                                  description: 'Unable to find imported account. Please ensure data was imported correctly.',
                                  variant: 'destructive',
                                });
                              }
                            }}
                          >
                            <Badge
                              variant={parsingConfigured ? 'default' : 'destructive'}
                              className={cn(
                                parsingConfigured ? 'bg-green-600' : '',
                                'cursor-pointer'
                              )}
                            >
                              {parsingConfigured ? '✓ Configured' : '❌ Missing - Click to Configure'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                validationStatus === 'valid' ? 'default' :
                                validationStatus === 'warning' ? 'secondary' :
                                validationStatus === 'invalid' ? 'destructive' : 'outline'
                              }
                            >
                              {validationStatus === 'valid' && '✓ Valid'}
                              {validationStatus === 'warning' && '⚠ Warning'}
                              {validationStatus === 'invalid' && '❌ Invalid'}
                              {!['valid', 'warning', 'invalid'].includes(validationStatus) && 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // #region agent log
                              fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1055',message:'Calculating Missing Fields',data:{rowId:row.id,dataFieldsMissing:missing.length,collectionConfigured,parsingConfigured},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                              // #endregion
                              // Combine missing data fields with missing configuration
                              const allMissing = [...missing];
                              if (!collectionConfigured) {
                                allMissing.push({ key: 'collection', label: 'Collection' });
                              }
                              if (!parsingConfigured) {
                                allMissing.push({ key: 'parsing', label: 'Parsing' });
                              }
                              // #region agent log
                              fetch('http://127.0.0.1:7242/ingest/3f73dd7b-a53e-443a-a45f-37c2454df9c5',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Stage3Validation.tsx:1064',message:'Missing Fields result',data:{rowId:row.id,totalMissing:allMissing.length,allMissingFields:allMissing.map(f=>f.key),isComplete:allMissing.length===0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
                              // #endregion
                              return allMissing.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {allMissing.map(f => (
                                    <Badge key={f.key} variant="destructive" className="text-xs">
                                      {f.label}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-green-600 text-sm">✓ Complete</span>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Data Summary</CardTitle>
          <CardDescription>Overview of imported entities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold">{insights?.total_properties || 0}</p>
              <p className="text-sm text-muted-foreground">Properties</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold">{insights?.total_units || 0}</p>
              <p className="text-sm text-muted-foreground">Units</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold">{insights?.total_accounts || 0}</p>
              <p className="text-sm text-muted-foreground">Accounts</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-2xl font-bold">{insights?.total_tenants || 0}</p>
              <p className="text-sm text-muted-foreground">Tenants</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Issues Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Data Quality Issues</CardTitle>
              <CardDescription>
                Review and resolve issues before proceeding
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Classification">Classification</SelectItem>
                  <SelectItem value="Configuration">Configuration</SelectItem>
                  <SelectItem value="Data Quality">Data Quality</SelectItem>
                  <SelectItem value="Relationships">Relationships</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="critical" className="w-full">
            <TabsList>
              <TabsTrigger value="critical">
                Critical Issues ({criticalIssues.length})
              </TabsTrigger>
              <TabsTrigger value="warnings">
                Warnings ({warnings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="critical" className="space-y-4 mt-4">
              {criticalIssues.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-600" />
                  <p className="text-lg font-medium">No Critical Issues</p>
                  <p className="text-sm">All data quality checks passed!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {criticalIssues.map((issue) => (
                    <Card
                      key={issue.id}
                      className="border-red-200 hover:border-red-300 transition-colors"
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <AlertCircle className="h-5 w-5 text-red-600" />
                              <h3 className="font-semibold text-lg">{issue.description}</h3>
                              <Badge variant="destructive">{issue.count} affected</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              Category: {issue.category} • Affects: {issue.affectedEntities.join(', ')}
                            </p>
                            <div className="flex gap-2">
                              {issue.canAutoFix && (
                                <Button
                                  size="sm"
                                  onClick={() => handleBulkClassification()}
                                  className="gap-2"
                                >
                                  <Zap className="h-4 w-4" />
                                  Launch Bulk Classification Tool
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedIssue(issue)}
                              >
                                Review & Fix
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="warnings" className="space-y-4 mt-4">
              {warnings.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-600" />
                  <p className="text-lg font-medium">No Warnings</p>
                  <p className="text-sm">All data looks good!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {warnings.map((issue) => (
                    <Card
                      key={issue.id}
                      className="border-amber-200 hover:border-amber-300 transition-colors"
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <AlertTriangle className="h-5 w-5 text-amber-600" />
                              <h3 className="font-semibold text-lg">{issue.description}</h3>
                              <Badge variant="outline" className="border-amber-600 text-amber-700">
                                {issue.count} affected
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              Category: {issue.category} • Affects: {issue.affectedEntities.join(', ')}
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedIssue(issue)}
                            >
                              Review Details
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Insights & Recommendations */}
      {insights && (
        <Card>
          <CardHeader>
            <CardTitle>Insights & Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Avg Units per Property</p>
                  <p className="font-semibold">
                    {insights.total_properties > 0
                      ? (insights.total_units / insights.total_properties).toFixed(1)
                      : '0'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Properties Complete</p>
                  <p className="font-semibold">
                    {insights.total_properties > 0
                      ? Math.floor((insights.total_properties * 0.71)).toFixed(0)
                      : '0'}{' '}
                    of {insights.total_properties}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Accounts with Bill Source</p>
                  <p className="font-semibold">
                    {insights.accounts_with_bill_source} / {insights.total_accounts}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Accounts with GL Code</p>
                  <p className="font-semibold">
                    {insights.accounts_with_gl_code} / {insights.total_accounts}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Configurations Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Account Configurations</CardTitle>
              <CardDescription>
                Configure collection and parsing settings for each account
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-sm">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>{accounts.filter(a => a.status === 'complete').length} Complete</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>{accounts.filter(a => a.status === 'partial').length} Partial</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>{accounts.filter(a => a.status === 'incomplete').length} Incomplete</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-4">
            <Select value={accountFilter} onValueChange={(v) => setAccountFilter(v as typeof accountFilter)}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search accounts..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedAccounts(new Set(accounts.map(a => a.id)))}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedAccounts.size === 0}
              onClick={() => {
                const selectedAccountList = accounts.filter(a => selectedAccounts.has(a.id));
                if (selectedAccountList.length > 0) {
                  setIsBulkModalOpen(true);
                }
              }}
            >
              Bulk Configure Mapping
            </Button>
          </div>

          {/* Accounts Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selectedAccounts.size === accounts.length && accounts.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedAccounts(new Set(accounts.map(a => a.id)));
                        } else {
                          setSelectedAccounts(new Set());
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead>Account #</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Commodity</TableHead>
                  <TableHead>Collection</TableHead>
                  <TableHead>Parsing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingAccounts ? (
                  // Loading skeleton
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell><div className="h-4 w-4 bg-muted animate-pulse rounded" /></TableCell>
                      <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                      <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                      <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                      <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                      <TableCell><div className="h-5 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                      <TableCell><div className="h-5 w-20 bg-muted animate-pulse rounded-full" /></TableCell>
                      <TableCell><div className="h-5 w-16 bg-muted animate-pulse rounded-full" /></TableCell>
                      <TableCell><div className="h-8 w-8 bg-muted animate-pulse rounded" /></TableCell>
                    </TableRow>
                  ))
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No accounts imported yet. Complete Stage 2 to import accounts.
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts
                  .filter(a => {
                    if (accountFilter !== 'all' && a.status !== accountFilter) return false;
                    if (accountSearch && !`${a.account_number} ${a.property_name} ${a.unit_number}`.toLowerCase().includes(accountSearch.toLowerCase())) return false;
                    return true;
                  })
                  .map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedAccounts.has(account.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedAccounts);
                            if (checked) {
                              newSet.add(account.id);
                            } else {
                              newSet.delete(account.id);
                            }
                            setSelectedAccounts(newSet);
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{account.account_number}</TableCell>
                      <TableCell>{account.property_name}</TableCell>
                      <TableCell>{account.unit_number}</TableCell>
                      <TableCell>{account.commodity_type}</TableCell>
                      <TableCell>
                        {account.collection_configured ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {account.parsing_configured ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            account.status === 'complete' ? 'default' :
                            account.status === 'partial' ? 'secondary' : 'destructive'
                          }
                        >
                          {account.status === 'complete' && '✓ Ready'}
                          {account.status === 'partial' && '⚠ Partial'}
                          {account.status === 'incomplete' && '❌ Missing'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingAccount({
                              id: account.id,
                              account_number: account.account_number,
                              property_name: account.property_name,
                              unit_number: account.unit_number,
                              commodity_type: account.commodity_type,
                            });
                            setIsModalOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Configuration Modal */}
      {isBulkModalOpen && (
        <BulkConfigurationModal
          isOpen={isBulkModalOpen}
          onClose={() => {
            setIsBulkModalOpen(false);
            setSelectedAccounts(new Set());
          }}
          accounts={accounts
            .filter(a => selectedAccounts.has(a.id))
            .map(a => ({
              id: a.id,
              account_number: a.account_number,
              property_name: a.property_name,
              unit_number: a.unit_number,
              commodity_type: a.commodity_type,
            }))}
          projectId={projectId || ''}
          workspaceId={currentWorkspace?.id}
          onSave={async (config, accountIds) => {
            try {
              // Use bulk save mutation for efficiency
              await bulkSaveMutation.mutateAsync({
                projectId: projectId || '',
                propertyName: 'Bulk Configuration', // Not applying to property, just using for bulk save
                config: {
                  collection_source: config.collection_source,
                  collection_frequency: config.collection_frequency,
                  collection_config: config.collection_config || {},
                  collection_completed: config.collection_completed || false,
                  parsing_fields: config.parsing_fields || [],
                  parsing_completed: config.parsing_completed || false,
                  configuration_status: (config.collection_completed && config.parsing_completed) ? 'complete' :
                    (config.collection_completed || config.parsing_completed) ? 'partial' : 'incomplete',
                },
                accountIds: accountIds,
              });

              setIsBulkModalOpen(false);
              setSelectedAccounts(new Set());
            } catch (error) {
              console.error('Failed to save bulk configuration:', error);
              throw error;
            }
          }}
          isSaving={saveConfigMutation.isPending || bulkSaveMutation.isPending}
        />
      )}

      {/* Configuration Modal */}
      {editingAccount && (
        <ConfigurationModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingAccount(null);
          }}
          account={editingAccount}
          configuration={accounts.find(a => a.id === editingAccount.id)?.configuration || null}
          projectId={projectId || ''}
          workspaceId={currentWorkspace?.id}
          onSave={async (config) => {
            try {
              // Save to database
              await saveConfigMutation.mutateAsync({
                project_id: projectId || '',
                imported_account_id: editingAccount.id,
                collection_source: config.collection_source,
                collection_frequency: config.collection_frequency,
                collection_config: config.collection_config || {},
                collection_completed: config.collection_completed || false,
                parsing_fields: config.parsing_fields || [],
                parsing_completed: config.parsing_completed || false,
                configuration_status: (config.collection_completed && config.parsing_completed) ? 'complete' :
                  (config.collection_completed || config.parsing_completed) ? 'partial' : 'incomplete',
                apply_to_property: config.apply_to_property || false,
              });

              // If apply_to_property, save to all accounts under same property
              if (config.apply_to_property && editingAccount.property_name) {
                const propertyAccounts = accounts.filter(
                  a => a.property_name === editingAccount.property_name && a.id !== editingAccount.id
                );
                
                if (propertyAccounts.length > 0) {
                  await bulkSaveMutation.mutateAsync({
                    projectId: projectId || '',
                    propertyName: editingAccount.property_name,
                    config: {
                      collection_source: config.collection_source,
                      collection_frequency: config.collection_frequency,
                      collection_config: config.collection_config || {},
                      collection_completed: config.collection_completed || false,
                      parsing_fields: config.parsing_fields || [],
                      parsing_completed: config.parsing_completed || false,
                      configuration_status: (config.collection_completed && config.parsing_completed) ? 'complete' :
                        (config.collection_completed || config.parsing_completed) ? 'partial' : 'incomplete',
                      apply_to_property: true,
                    },
                    accountIds: propertyAccounts.map(a => a.id),
                  });
                }
              }
            } catch (error) {
              console.error('Failed to save configuration:', error);
            }
          }}
          isSaving={saveConfigMutation.isPending || bulkSaveMutation.isPending}
        />
      )}

      {/* Bulk Classification Tool Modal */}
      {bulkClassificationMode && selectedIssue?.id === 'missing_utility_type' && (
        <BulkClassificationTool
          issue={selectedIssue}
          onClose={() => {
            setBulkClassificationMode(false);
            setSelectedIssue(null);
          }}
          onComplete={() => {
            setBulkClassificationMode(false);
            setSelectedIssue(null);
            loadInsights();
          }}
        />
      )}

      {/* Export/Re-import Panel for data consistency */}
      {importBatchId && (
        <ExportReimportPanel 
          batchId={importBatchId} 
          onDataUpdated={() => loadStagedData()} 
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-2`)}
        >
          ← Previous Stage
        </Button>
        <Button
          onClick={() => {
            if (project && mandatoryFieldIssues.length === 0) {
              // Check if all accounts have parsing and collection mapping configured
              if (!mappingCompletionStatus.allComplete) {
                toast({
                  title: 'Parsing & Collection Mapping Required',
                  description: `Please configure parsing and collection mapping for all accounts. ${mappingCompletionStatus.incomplete} accounts are missing both, ${mappingCompletionStatus.partial} are partially configured.`,
                  variant: 'destructive',
                });
                return;
              }
              
              onboardingApi.updateProject(project.id, {
                current_stage: 'mapping',
              });
              navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-4`);
            } else {
              toast({
                title: 'Mandatory Fields Missing',
                description: 'Please fix all mandatory field issues before proceeding',
                variant: 'destructive',
              });
            }
          }}
          disabled={mandatoryFieldIssues.length > 0 || !mappingCompletionStatus.allComplete}
        >
          Continue to Configuration →
        </Button>
      </div>
    </div>
  );
};

// Bulk Classification Tool Component
interface BulkClassificationToolProps {
  issue: DataQualityIssue;
  onClose: () => void;
  onComplete: () => void;
}

const BulkClassificationTool = ({ issue, onComplete, onClose }: BulkClassificationToolProps) => {
  const [classificationMode, setClassificationMode] = useState<'wizard' | 'bulk' | 'manual'>('bulk');
  const [supplierGroups, setSupplierGroups] = useState<Array<{
    supplier: string;
    count: number;
    suggestedType: string;
    confidence: number;
  }>>([
    { supplier: 'ABC Electric Company', count: 289, suggestedType: 'Electric', confidence: 98 },
    { supplier: 'City Gas & Power', count: 87, suggestedType: 'Gas', confidence: 96 },
    { supplier: 'Metropolitan Water District', count: 67, suggestedType: 'Water', confidence: 99 },
    { supplier: 'Multiple Suppliers Inc', count: 154, suggestedType: 'Mixed', confidence: 72 },
  ]);
  const [classifications, setClassifications] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);

  const handleApplyClassification = async (supplier: string, utilityType: string) => {
    setClassifications((prev) => ({
      ...prev,
      [supplier]: utilityType,
    }));
  };

  const handleApplyAll = async () => {
    setProcessing(true);
    try {
      // TODO: Apply classifications via backend
      await new Promise((resolve) => setTimeout(resolve, 2000));
      onComplete();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="border-2 border-primary">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Bulk Utility Type Classification</CardTitle>
            <CardDescription>
              Classify {issue.count} accounts by supplier
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ×
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex gap-4">
          <Button
            variant={classificationMode === 'bulk' ? 'default' : 'outline'}
            onClick={() => setClassificationMode('bulk')}
            size="sm"
          >
            Bulk Mode
          </Button>
          <Button
            variant={classificationMode === 'wizard' ? 'default' : 'outline'}
            onClick={() => setClassificationMode('wizard')}
            size="sm"
          >
            Wizard Mode
          </Button>
          <Button
            variant={classificationMode === 'manual' ? 'default' : 'outline'}
            onClick={() => setClassificationMode('manual')}
            size="sm"
          >
            Manual Mode
          </Button>
        </div>

        {classificationMode === 'bulk' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Group accounts by supplier and assign utility types in bulk
            </p>
            <div className="space-y-3">
              {supplierGroups.map((group) => (
                <Card key={group.supplier} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="font-medium">{group.supplier}</span>
                        <Badge variant="outline">{group.count} accounts</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Suggested: {group.suggestedType} (Confidence: {group.confidence}%)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={classifications[group.supplier] || group.suggestedType}
                        onValueChange={(value) => handleApplyClassification(group.supplier, value)}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Electric">Electric</SelectItem>
                          <SelectItem value="Gas">Gas</SelectItem>
                          <SelectItem value="Water">Water</SelectItem>
                          <SelectItem value="Sewer">Sewer</SelectItem>
                          <SelectItem value="Trash">Trash</SelectItem>
                          <SelectItem value="Mixed">Mixed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={() => handleApplyClassification(group.supplier, classifications[group.supplier] || group.suggestedType)}
                      >
                        Apply to All {group.count}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApplyAll} disabled={processing}>
            {processing ? 'Processing...' : 'Apply All Classifications'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Stage3Validation;

